import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { DEMO_DECISION_TWINS_V2, DEMO_EVIDENCE_EVENTS, DEMO_VOTING_RECORDS, DEMO_DECISION_TWIN_BIO_STATES } from '../api/demoData';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';

const DECISION_GLOSSARY: GlossaryEntry[] = [
  { term: 'dt-', definition: 'Decision Twin identifier' },
  { term: 'SHA-256', definition: 'Cryptographic hash \u2014 unique fingerprint of the complete decision state' },
  { term: 'Tamper-evident', definition: 'Any change to underlying data changes the hash immediately' },
  { term: 'Omissions', definition: 'Evidence that was missing or incomplete at time of committee vote' },
  { term: 'Complete', definition: 'All required evidence present at vote time' },
  { term: 'Acknowledged', definition: 'Vote proceeded with known evidence gaps, gaps documented' },
  { term: 'Quorum', definition: 'Minimum committee members required to make a valid vote (4 of 5)' },
  { term: 'Unanimous', definition: 'All members voted the same way' },
  { term: 'Split Vote', definition: 'Members voted differently \u2014 dissent is recorded in audit trail' },
  { term: 'JSON-LD', definition: 'Linked data export format for regulatory submission' },
  { term: 'M15 Bio State', definition: 'Biological state model score' },
  { term: 'M16 Heartfulness', definition: 'Cardiac coherence index model score' },
  { term: 'M17 Dietary', definition: 'Food-drug interaction flag model score' },
  { term: 'M18 Psych', definition: 'Psychological wellbeing model score' },
  { term: 'Stop', definition: 'Committee vote to halt dose escalation \u2014 DLT threshold reached' },
  { term: 'MTD', definition: 'Maximum Tolerated Dose \u2014 highest dose where DLTs remain below threshold' },
  { term: 'RP2D', definition: 'Recommended Phase 2 Dose \u2014 typically set at or below MTD' },
];

const SLIDER_MIN = new Date('2026-01-15T00:00:00Z').getTime();
const SLIDER_MAX = new Date('2026-02-15T23:59:59Z').getTime();
const MS_PER_DAY = 86_400_000;

const EVENT_TYPE_COLORS: Record<string, string> = {
  'Vital Signs': '#48bb78',
  'Lab Result': '#63b3ed',
  'AE Report': '#ed8936',
  'SAE Report': '#f56565',
  'PK Sample': '#b794f4',
  'ECG': '#4fd1c5',
};

function getTrialPhase(ts: number): string {
  const day = Math.floor((ts - SLIDER_MIN) / MS_PER_DAY);
  if (day < 5) return 'Dosing';
  if (day < 12) return 'Observation';
  return 'Review';
}

function getCurrentCohort(ts: number): string {
  const day = Math.floor((ts - SLIDER_MIN) / MS_PER_DAY);
  return day < 8 ? 'Cohort 1 (10mg)' : 'Cohort 2 (25mg)';
}

const VOTE_COLORS: Record<string, { bg: string; text: string }> = {
  'Go': { bg: '#22543d', text: '#48bb78' },
  'No-Go': { bg: '#744210', text: '#ecc94b' },
  'Abstain': { bg: '#2d3748', text: '#a0aec0' },
  'Stop': { bg: '#742a2a', text: '#fc8181' },
};

export default function DecisionTwins() {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [sliderValue, setSliderValue] = useState(SLIDER_MAX);
  const [showCsrModal, setShowCsrModal] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const toggle = (id: string) => setExpanded(expanded === id ? null : id);

  // Play/pause logic
  const stopPlayback = useCallback(() => {
    setIsPlaying(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPlayback = useCallback(() => {
    setSliderValue(SLIDER_MIN);
    setIsPlaying(true);
  }, []);

  useEffect(() => {
    if (!isPlaying) return;
    intervalRef.current = setInterval(() => {
      setSliderValue((prev) => {
        const next = prev + MS_PER_DAY;
        if (next >= SLIDER_MAX) {
          stopPlayback();
          return SLIDER_MAX;
        }
        return next;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPlaying, stopPlayback]);

  const selectedDate = new Date(sliderValue);

  // Filter data by slider
  const visibleEvidence = DEMO_EVIDENCE_EVENTS.filter(
    (e) => new Date(e.internalKnowledgeTimestamp).getTime() <= sliderValue
  );

  const chartData = DEMO_EVIDENCE_EVENTS.map((e) => ({
    effectiveTs: new Date(e.effectiveTimestamp).getTime(),
    knowledgeTs: new Date(e.internalKnowledgeTimestamp).getTime(),
    eventType: e.eventType,
    id: e.id,
    subject: e.subject,
    visible: new Date(e.internalKnowledgeTimestamp).getTime() <= sliderValue,
  }));

  // Interpolate model scores at slider time
  const getScoresAtTime = (twin: typeof DEMO_DECISION_TWINS_V2[0]) => {
    const twinTime = new Date(twin.captured_at).getTime();
    if (sliderValue >= twinTime) return twin.model_scores;
    // Before twin was captured: scale proportionally
    const progress = Math.max(0, (sliderValue - SLIDER_MIN) / (twinTime - SLIDER_MIN));
    const scaled: Record<string, number> = {};
    for (const [k, v] of Object.entries(twin.model_scores)) {
      scaled[k] = Number((v * progress).toFixed(2));
    }
    return scaled;
  };

  const getEvidenceCountAtTime = () => visibleEvidence.length;
  const getOmissionCountAtTime = () => {
    const total = DEMO_EVIDENCE_EVENTS.length;
    return Math.max(0, total - visibleEvidence.length);
  };

  const exportJsonLd = (twin: typeof DEMO_DECISION_TWINS_V2[0]) => {
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Action',
      name: 'DecisionStateCapture',
      identifier: twin.twin_id,
      object: {
        '@type': 'Dataset',
        identifier: twin.trial_id,
        dateCreated: twin.captured_at,
        additionalProperty: [
          { '@type': 'PropertyValue', name: 'snapshot_hash', value: twin.snapshot_hash },
          { '@type': 'PropertyValue', name: 'evidence_count', value: twin.evidence_count },
          { '@type': 'PropertyValue', name: 'omitted_count', value: twin.omitted_count },
          ...Object.entries(twin.model_scores).map(([k, v]) => ({
            '@type': 'PropertyValue', name: k, value: v,
          })),
        ],
      },
      agent: { '@type': 'Person', identifier: twin.user_id },
    };
    const blob = new Blob([JSON.stringify(jsonLd, null, 2)], { type: 'application/ld+json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `decision-twin-${twin.twin_id}.jsonld`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const activeTwins = DEMO_DECISION_TWINS_V2.filter(
    (t) => new Date(t.captured_at).getTime() <= sliderValue
  );

  return (
    <div style={styles.container}>
      <Glossary entries={DECISION_GLOSSARY} />
      <h2 style={styles.title}>Decision Twin Audit Trail</h2>
      <p style={styles.desc}>
        Each decision twin is an immutable snapshot of the complete evidence state
        at the time of a committee vote. SHA-256 hashes ensure tamper-evidence.
      </p>
      <ComplianceBadges badges={["21 CFR Part 11", "ICH E6(R3)", "MHRA Apr 2026", "UK CTR 2025"]} />

      {/* Bi-temporal time slider */}
      <div style={styles.sliderSection}>
        <div style={styles.sliderHeader}>
          <h3 style={styles.sectionTitle}>Bi-Temporal Projection</h3>
          <button
            style={styles.playBtn}
            onClick={isPlaying ? stopPlayback : startPlayback}
          >
            {isPlaying ? '\u23F8 Pause' : '\u25B6 Play'}
          </button>
        </div>
        <input
          type="range"
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          value={sliderValue}
          onChange={(e) => {
            stopPlayback();
            setSliderValue(Number(e.target.value));
          }}
          style={styles.slider}
        />
        <div style={styles.sliderLabels}>
          <span>Jan 15, 2026</span>
          <span style={styles.sliderCurrent}>
            Viewing trial state as of {selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} {selectedDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <span>Feb 15, 2026</span>
        </div>

        {/* Status panel */}
        <div style={styles.statusRow}>
          <div style={styles.statusCard}>
            <div style={styles.statusLabel}>Decision Twins</div>
            <div style={styles.statusValue}>{activeTwins.length}</div>
          </div>
          <div style={styles.statusCard}>
            <div style={styles.statusLabel}>Evidence Recorded</div>
            <div style={styles.statusValue}>{getEvidenceCountAtTime()}</div>
          </div>
          <div style={styles.statusCard}>
            <div style={styles.statusLabel}>Trial Phase</div>
            <div style={styles.statusValue}>{getTrialPhase(sliderValue)}</div>
          </div>
          <div style={styles.statusCard}>
            <div style={styles.statusLabel}>Current Cohort</div>
            <div style={styles.statusValue}>{getCurrentCohort(sliderValue)}</div>
          </div>
        </div>

        {/* Timeline chart */}
        <ResponsiveContainer width="100%" height={180}>
          <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
            <XAxis
              type="number"
              dataKey="effectiveTs"
              domain={[SLIDER_MIN, SLIDER_MAX]}
              tickFormatter={(v: number) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              tick={{ fill: '#a0aec0', fontSize: 10 }}
              name="Effective"
            />
            <YAxis
              type="number"
              dataKey="knowledgeTs"
              domain={[SLIDER_MIN, SLIDER_MAX]}
              tickFormatter={(v: number) => new Date(v).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              tick={{ fill: '#a0aec0', fontSize: 10 }}
              name="Recorded"
              width={60}
            />
            <Tooltip
              contentStyle={{ backgroundColor: '#1a202c', border: '1px solid #2d3748', color: '#e2e8f0', fontSize: 12 }}
              formatter={(value: any, name: any) => [
                new Date(value).toLocaleString(),
                String(name),
              ]}
            />
            <ReferenceLine
              x={sliderValue}
              stroke="#63b3ed"
              strokeWidth={2}
              strokeDasharray="4 4"
              label={{ value: 'Now', position: 'top', fill: '#63b3ed', fontSize: 11 }}
            />
            {DEMO_DECISION_TWINS_V2.map((t) => (
              <ReferenceLine
                key={t.twin_id}
                x={new Date(t.captured_at).getTime()}
                stroke={new Date(t.captured_at).getTime() <= sliderValue ? '#ecc94b' : '#2d3748'}
                strokeDasharray="2 2"
              />
            ))}
            <Scatter data={chartData} shape="circle">
              {chartData.map((entry, i) => (
                <Cell
                  key={i}
                  fill={entry.visible ? (EVENT_TYPE_COLORS[entry.eventType] || '#a0aec0') : '#2d3748'}
                  opacity={entry.visible ? 1 : 0.3}
                  r={entry.visible ? 5 : 3}
                />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>

      {/* Decision Twin cards */}
      {DEMO_DECISION_TWINS_V2.map((twin) => {
        const isCaptured = new Date(twin.captured_at).getTime() <= sliderValue;
        const votingRecord = DEMO_VOTING_RECORDS.find((v) => v.twinId === twin.twin_id);
        const scores = isCaptured ? twin.model_scores : getScoresAtTime(twin);
        const evidenceCount = isCaptured ? twin.evidence_count : getEvidenceCountAtTime();
        const omissionCount = isCaptured ? twin.omitted_count : getOmissionCountAtTime();

        return (
          <div key={twin.twin_id} style={{
            ...styles.card,
            opacity: isCaptured ? 1 : 0.4,
            pointerEvents: isCaptured ? 'auto' : 'none',
          }}>
            <div style={styles.cardHeader} onClick={() => isCaptured && toggle(twin.twin_id)}>
              <div style={styles.cardLeft}>
                <span style={styles.twinId}>{twin.twin_id}</span>
                <span style={styles.date}>{new Date(twin.captured_at).toLocaleString()}</span>
                {!isCaptured && (
                  <span style={styles.notCapturedBadge}>Not yet captured</span>
                )}
              </div>
              <div style={styles.cardRight}>
                <span style={styles.stat}>Evidence: {evidenceCount}</span>
                <span style={{
                  ...styles.stat,
                  color: omissionCount > 0 ? '#ed8936' : '#48bb78',
                }}>
                  Omissions: {omissionCount}
                </span>
                <span style={{
                  ...styles.ackBadge,
                  backgroundColor: twin.ack_status === 'complete' ? '#22543d' : '#744210',
                  color: twin.ack_status === 'complete' ? '#48bb78' : '#ecc94b',
                }}>
                  {twin.ack_status}
                </span>
                <span style={styles.expand}>{expanded === twin.twin_id ? '\u25B2' : '\u25BC'}</span>
              </div>
            </div>

            {expanded === twin.twin_id && isCaptured && (
              <div style={styles.cardBody}>
                <div style={styles.hashRow}>
                  <span style={styles.hashLabel}>SHA-256:</span>
                  <code style={styles.hash}>{twin.snapshot_hash}</code>
                </div>
                <div style={styles.row}>
                  <span style={styles.label}>Captured by:</span>
                  <span style={styles.value}>{twin.user_id}</span>
                </div>
                <div style={styles.scoresGrid}>
                  {Object.entries(scores).map(([k, v]) => (
                    <div key={k} style={styles.scoreCard}>
                      <div style={styles.scoreLabel}>{k}</div>
                      <div style={styles.scoreValue}>{(v * 100).toFixed(0)}%</div>
                    </div>
                  ))}
                </div>
                {twin.omitted_ids.length > 0 && (
                  <div style={styles.omissions}>
                    <div style={styles.label}>Omitted Evidence IDs:</div>
                    {twin.omitted_ids.map((id) => (
                      <code key={id} style={styles.omId}>{id}</code>
                    ))}
                  </div>
                )}

                {/* Voting Record */}
                {votingRecord && (
                  <div style={styles.votingSection}>
                    <div style={styles.votingHeader}>
                      <h4 style={styles.votingSectionTitle}>Voting Record</h4>
                      <span style={{
                        ...styles.quorumBadge,
                        backgroundColor: votingRecord.unanimous ? '#22543d' : '#744210',
                        color: votingRecord.unanimous ? '#48bb78' : '#ecc94b',
                      }}>
                        {votingRecord.unanimous ? 'Unanimous' : 'Split Vote'}
                      </span>
                      <span style={styles.quorumText}>
                        Quorum: {votingRecord.quorumPresent} of {votingRecord.quorumRequired + 1} present ({votingRecord.quorumRequired} required)
                      </span>
                    </div>
                    <table style={styles.voteTable}>
                      <thead>
                        <tr>
                          <th style={styles.voteTh}>Member</th>
                          <th style={styles.voteTh}>Role</th>
                          <th style={styles.voteTh}>Vote</th>
                          <th style={styles.voteTh}>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {votingRecord.votes.map((v) => (
                          <tr key={v.name}>
                            <td style={styles.voteTd}>{v.name}</td>
                            <td style={{ ...styles.voteTd, color: '#a0aec0', fontSize: 12 }}>{v.role}</td>
                            <td style={styles.voteTd}>
                              <span style={{
                                ...styles.voteBadge,
                                backgroundColor: VOTE_COLORS[v.vote].bg,
                                color: VOTE_COLORS[v.vote].text,
                              }}>
                                {v.vote}
                              </span>
                            </td>
                            <td style={{ ...styles.voteTd, color: '#718096', fontSize: 12 }}>
                              {new Date(v.votedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Phase 7 — Biological State at Vote Time */}
                {(() => {
                  const bioState = DEMO_DECISION_TWIN_BIO_STATES.find((b) => b.twinId === twin.twin_id);
                  if (!bioState) return null;
                  return (
                    <div style={styles.bioSection}>
                      <div style={styles.bioHeader}>
                        <h4 style={styles.votingSectionTitle}>Patient Biological State at Vote Time</h4>
                        {!bioState.fullyAcknowledged && (
                          <span style={styles.bioWarningBadge}>
                            Biological state not fully acknowledged
                          </span>
                        )}
                      </div>
                      <table style={styles.voteTable}>
                        <thead>
                          <tr>
                            <th style={styles.voteTh}>Patient</th>
                            <th style={styles.voteTh}>Risk Modifier</th>
                            <th style={styles.voteTh}>Key Flags</th>
                            <th style={styles.voteTh}>Acknowledged</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bioState.patientStates.map((ps) => {
                            const riskColor = ps.compositeRiskModifier <= 15 ? '#48bb78' : ps.compositeRiskModifier <= 30 ? '#ecc94b' : '#f56565';
                            return (
                              <tr key={ps.patientId} style={
                                !ps.acknowledged ? { backgroundColor: 'rgba(236, 201, 75, 0.08)' } : {}
                              }>
                                <td style={styles.voteTd}>{ps.patientId}</td>
                                <td style={styles.voteTd}>
                                  <span style={{
                                    color: riskColor,
                                    fontWeight: 700,
                                    fontSize: 14,
                                  }}>+{ps.compositeRiskModifier}%</span>
                                </td>
                                <td style={{ ...styles.voteTd, maxWidth: 300 }}>
                                  {ps.keyFlags.length > 0 ? (
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                      {ps.keyFlags.map((flag) => (
                                        <span key={flag} style={{
                                          backgroundColor: '#2d3748',
                                          color: '#e2e8f0',
                                          padding: '2px 8px',
                                          borderRadius: 4,
                                          fontSize: 11,
                                        }}>{flag}</span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span style={{ color: '#4a5568', fontSize: 12 }}>None</span>
                                  )}
                                </td>
                                <td style={styles.voteTd}>
                                  {ps.acknowledged ? (
                                    <span style={{
                                      backgroundColor: '#22543d',
                                      color: '#48bb78',
                                      padding: '2px 10px',
                                      borderRadius: 12,
                                      fontSize: 11,
                                      fontWeight: 600,
                                    }}>Yes</span>
                                  ) : (
                                    <span style={{
                                      backgroundColor: '#744210',
                                      color: '#ecc94b',
                                      padding: '2px 10px',
                                      borderRadius: 12,
                                      fontSize: 11,
                                      fontWeight: 600,
                                    }}>No</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                  <button style={styles.exportBtn} onClick={() => exportJsonLd(twin)}>
                    Export JSON-LD
                  </button>
                  {twin.twin_id === 'dt-003' && (
                    <button style={{ ...styles.exportBtn, backgroundColor: '#2f855a' }} onClick={() => setShowCsrModal(true)}>
                      Export CSR
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* CSR Export Modal */}
      {showCsrModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCsrModal(false)}>
          <div style={styles.modalPanel} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Clinical Study Report Export</h3>
              <button onClick={() => setShowCsrModal(false)} style={styles.modalClose}>&times;</button>
            </div>
            <div style={styles.modalBody}>
              <div style={{ color: '#63b3ed', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>ICH E3 Format</div>
              <p style={{ color: '#cbd5e0', fontSize: 13, lineHeight: 1.6, margin: '0 0 16px' }}>
                Generating patient data listings, AE summary tables, PK parameter tables,
                deviation log, and MTD declaration for regulatory submission.
              </p>
              <div style={{
                backgroundColor: 'rgba(99, 179, 237, 0.1)', border: '1px solid #2b6cb0',
                borderRadius: 8, padding: '12px 16px', color: '#63b3ed', fontSize: 12,
              }}>
                This feature is available in the enterprise tier.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 32 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 },
  desc: { color: '#a0aec0', fontSize: 14, marginBottom: 24, maxWidth: 700 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#cbd5e0', margin: 0 },

  // Slider section
  sliderSection: { backgroundColor: '#1a202c', borderRadius: 8, padding: 20, marginBottom: 24, border: '1px solid #2d3748' },
  sliderHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  playBtn: {
    padding: '6px 16px', backgroundColor: '#2b6cb0', color: '#fff', border: 'none',
    borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  slider: { width: '100%', cursor: 'pointer', accentColor: '#63b3ed' },
  sliderLabels: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#718096', fontSize: 11, marginTop: 4 },
  sliderCurrent: { color: '#63b3ed', fontWeight: 600, fontSize: 13 },

  // Status row
  statusRow: { display: 'flex', gap: 12, marginTop: 16, marginBottom: 16, flexWrap: 'wrap' },
  statusCard: { backgroundColor: '#171923', borderRadius: 6, padding: '10px 16px', flex: 1, minWidth: 120 },
  statusLabel: { color: '#718096', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 },
  statusValue: { color: '#e2e8f0', fontSize: 18, fontWeight: 700, marginTop: 2 },

  // Cards
  card: { backgroundColor: '#1a202c', borderRadius: 8, marginBottom: 16, overflow: 'hidden', border: '1px solid #2d3748', transition: 'opacity 0.3s' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', cursor: 'pointer' },
  cardLeft: { display: 'flex', flexDirection: 'column', gap: 4 },
  cardRight: { display: 'flex', alignItems: 'center', gap: 16 },
  twinId: { color: '#63b3ed', fontWeight: 600, fontSize: 14 },
  date: { color: '#718096', fontSize: 12 },
  notCapturedBadge: { color: '#a0aec0', fontSize: 11, fontStyle: 'italic', backgroundColor: '#2d3748', padding: '2px 8px', borderRadius: 4, marginTop: 2, display: 'inline-block' },
  stat: { color: '#a0aec0', fontSize: 13 },
  ackBadge: { padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
  expand: { color: '#a0aec0', fontSize: 12 },
  cardBody: { padding: '0 20px 20px', borderTop: '1px solid #2d3748' },
  hashRow: { marginTop: 16, display: 'flex', alignItems: 'center', gap: 8 },
  hashLabel: { color: '#a0aec0', fontSize: 12, fontWeight: 600 },
  hash: { color: '#48bb78', fontSize: 11, fontFamily: 'monospace', backgroundColor: '#171923', padding: '4px 8px', borderRadius: 4, wordBreak: 'break-all' },
  row: { marginTop: 12, display: 'flex', gap: 8 },
  label: { color: '#a0aec0', fontSize: 12, fontWeight: 600 },
  value: { color: '#e2e8f0', fontSize: 13 },
  scoresGrid: { display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' },
  scoreCard: { backgroundColor: '#171923', borderRadius: 6, padding: '10px 16px', minWidth: 100 },
  scoreLabel: { color: '#718096', fontSize: 11, textTransform: 'uppercase' },
  scoreValue: { color: '#e2e8f0', fontSize: 20, fontWeight: 700 },
  omissions: { marginTop: 16 },
  omId: { display: 'inline-block', marginRight: 8, marginTop: 4, color: '#ed8936', fontSize: 12, fontFamily: 'monospace', backgroundColor: '#171923', padding: '2px 8px', borderRadius: 4 },

  // Voting section
  votingSection: { marginTop: 20, borderTop: '1px solid #2d3748', paddingTop: 16 },
  votingHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  votingSectionTitle: { fontSize: 14, fontWeight: 600, color: '#cbd5e0', margin: 0 },
  quorumBadge: { padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700 },
  quorumText: { color: '#718096', fontSize: 12 },
  voteTable: { width: '100%', borderCollapse: 'collapse' },
  voteTh: { textAlign: 'left', padding: '6px 10px', borderBottom: '1px solid #2d3748', color: '#718096', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 },
  voteTd: { padding: '8px 10px', borderBottom: '1px solid #171923', color: '#e2e8f0', fontSize: 13 },
  voteBadge: { padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },

  // Biological state section
  bioSection: { marginTop: 20, borderTop: '1px solid #2d3748', paddingTop: 16 },
  bioHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  bioWarningBadge: { backgroundColor: '#744210', color: '#ecc94b', padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700 },

  exportBtn: { padding: '8px 20px', backgroundColor: '#2b6cb0', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },

  // CSR Modal
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modalPanel: { backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 12, width: 480, maxWidth: '90vw', overflow: 'hidden', boxShadow: '0 12px 40px rgba(0,0,0,0.5)' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #2d3748' },
  modalTitle: { fontSize: 16, fontWeight: 700, color: '#e2e8f0', margin: 0 },
  modalClose: { background: 'none', border: 'none', color: '#a0aec0', fontSize: 24, cursor: 'pointer', padding: 0, lineHeight: 1 },
  modalBody: { padding: '20px' },
};
